import React from 'react';
import styled from 'styled-components';
import { MediaType } from '@kouto/types';
import { useAppState } from 'AppProvider';
import ResponsiveLayout from 'ResponsiveLayout';
import MinimalVideoPlayer from 'components/MinimalVideoPlayer/MinimalVideoPlayer';
import { getBiggerMediaLink } from 'utils/listings';
import { Listing } from 'types/listings';
import { ReducerState } from 'types/reducer';

type BrandHeroMedia = Listing['medias'][number];

interface ResolvedHeroMedia {
  readonly url: string;
  readonly type: BrandHeroMedia['type'];
}

/** Resolve a brand hero media to a usable absolute URL, or null (so we render no hero). */
const resolveHeroMedia = (media?: BrandHeroMedia): ResolvedHeroMedia | null => {
  if (!media) return null;
  const url = getBiggerMediaLink(media);
  if (!url || !url.startsWith('http')) return null;
  return { url, type: media.type };
};

interface HeroMediaLayerProps {
  readonly media: ResolvedHeroMedia | null;
}

/** The brand hero image/video used as the schedule hero (same source as the home hero). */
const HeroMediaLayer = ({ media }: HeroMediaLayerProps) => {
  if (!media) return null;

  if (media.type === MediaType.VIDEO) {
    return (
      <MinimalVideoPlayer
        videoUrl={media.url}
        loop
        play
        showPlayPauseButton
        showMuteButton={false}
        className="schedule-view__hero-video"
      />
    );
  }

  return (
    <HeroImage
      className="schedule-view__hero-image"
      src={media.url}
      alt=""
      aria-hidden="true"
    />
  );
};

/**
 * Schedule hero: the brand's configured hero media (matching the home landing), with no text
 * overlay. Renders nothing when the brand has no usable hero media, like the home hero.
 */
const ScheduleHero = () => {
  const settings = useAppState((state: ReducerState) => state.brand.settings);
  const desktop = resolveHeroMedia(
    settings?.templateElement?.hero?.desktopMedia as BrandHeroMedia | undefined,
  );
  const mobile = resolveHeroMedia(
    settings?.templateElement?.hero?.mobileMedia as BrandHeroMedia | undefined,
  );

  if (!desktop && !mobile) return null;

  const renderDesktop = () => <HeroMediaLayer media={desktop} />;
  const renderMobile = () => <HeroMediaLayer media={mobile || desktop} />;

  return (
    <Hero className="schedule-view__hero">
      <HeroBackdrop className="schedule-view__hero-media" aria-hidden>
        <ResponsiveLayout
          breakPoint={767}
          renderDesktop={renderDesktop}
          renderMobile={renderMobile}
        />
      </HeroBackdrop>
    </Hero>
  );
};

export default ScheduleHero;

/* ---- hero (brand media only, no text overlay — matches the home hero) ---- */
/* Sized by aspect-ratio to match the landing page's <Hero> (`components/Hero/Hero.js`
   `WrapperHero`), so the same brand media renders at the same height on both surfaces:
   100:40 letterbox above the mobile breakpoint, 100:150 portrait below it. `margin-bottom`
   also mirrors WrapperHero so the gap to the content column below matches the landing page. */
const Hero = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 100 / 150;
  margin-bottom: 48px;
  /* Neutral placeholder shown only until the brand image loads. */
  background: linear-gradient(
    162deg,
    var(--way-colors-contrastColorShades-5) 13%,
    var(--way-colors-contrastColorShades-40) 78%
  );

  @media all and (min-width: 768px) {
    aspect-ratio: 100 / 40;
  }
`;

const HeroBackdrop = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  right: 0;
`;
